// ObsidianClaimEvidence — Tier-A noir renderer for the claim-evidence component.
//
// A React port of the "OBSIDIAN DOSSIER" design-exploration mockup
// (design-explorations/scenario-themes/obsidian-dossier/index.html), driven by
// the REAL claim-evidence data + the ClaimEvidenceStudent state machine. The
// second consumer of the shared theme-world toolkit (lib/scenario-themes/):
// atmosphere, audio, sound toggle, and typewriter all come from there.
//
// State machine + onActivityResponse contract mirror ClaimEvidenceSlide's
// `ClaimEvidenceStudent` exactly: a `selected` Set (min/max gated), `reasoning`
// string, `submitted` flag; on submit it emits the EXACT
//   { activityType:'claim-evidence', mode:'quiz',
//     responseData:{ selectedEvidence, reasoning } }
// payload, and scores via set-overlap against expertModel.selectedEvidence.
//
// ★ THE STANDOUT MOMENT ★ — the red string. Pinning a card unspools a length of
// crimson twine (SVG stroke-dashoffset) from the card to the central CLAIM node,
// with a soft pin-push click. Closing the case slams a CASE CLOSED stamp and
// lifts redaction bars to unseal the expert verdict.
//
// The verdict is STAGED (§8.1b: one idea-beat at a time, no wall-of-text
// endings). First render shows only the stamp + the score line; the remaining
// sections sit behind redaction seals the learner taps to lift, in order:
// your picks judged → the evidence that holds → the counter-claim → reflect +
// reopen. Lifted sections accumulate; each lift plays the paper-rustle cue.
// The final reflection is a real captured commit (not display-only): saving it
// re-emits the full claim-evidence record with the reflection appended.

import { useState, useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { ClaimEvidenceSlideData, Theme } from '../../slideTypes';
import type { ActivityResponsePayload } from '../../../../../types';
import { feedbackStyle } from '../../shared/SlideShared';
import { usePrefersReducedMotion } from '../../../../../hooks/usePrefersReducedMotion';
import { ThemeWorldAtmosphere } from '../../../../../lib/scenario-themes/Atmosphere';
import { useThemeWorldAudio } from '../../../../../lib/scenario-themes/audio';
import { SoundToggle } from '../../../../../lib/scenario-themes/SoundToggle';
import { Typewriter } from '../../../../../lib/scenario-themes/Typewriter';

type Strength = 'strong' | 'partial' | 'distractor';

const STYLE_ID = 'obsidian-ce-scoped-styles';

const STRENGTH_LABEL: Record<Strength, string> = {
  strong: 'Strong',
  partial: 'Partial',
  distractor: 'Off-track',
};

// Staged-verdict sections (§8.1b) — the idea-beats behind the redaction seals,
// lifted in this fixed order. 'counter' is skipped when no counter-claim is
// authored. Personal first: the learner's own picks before the expert model.
type SealKey = 'judged' | 'evidence' | 'counter' | 'reflect';

const SEAL_LABEL: Record<SealKey, string> = {
  judged: 'Your picks, judged',
  evidence: 'The evidence that holds',
  counter: 'The counter-claim',
  reflect: 'Reflect and reopen',
};

// ─────────────────────────────────────────────────────────────────────────────
// Component-specific scoped CSS — corkboard, paper cards, red strings, stamp,
// redaction reveal. Namespaced under `.obsce`; injected once, ref-counted.
// (Atmosphere layers live in the toolkit's <ThemeWorldAtmosphere>.)
// ─────────────────────────────────────────────────────────────────────────────

function scopedCss(amber: string, brass: string, oxblood: string, twine: string, bone: string): string {
  return `
.obsce { position: relative; }
.obsce * { box-sizing: border-box; }

/* ===== RED STRINGS ===== */
.obsce .obs-strings { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; z-index: 30; overflow: visible; }
.obsce .obs-strings path {
  fill: none; stroke: ${twine}; stroke-width: 2.2; stroke-linecap: round;
  filter: drop-shadow(0 2px 2px rgba(0,0,0,.7));
  stroke-dasharray: var(--len); stroke-dashoffset: var(--len);
  transition: stroke-dashoffset 1.1s cubic-bezier(.6,.05,.2,1);
}
.obsce .obs-strings path.obs-drawn { stroke-dashoffset: 0; }

/* ===== EVIDENCE CARD (printed paper) ===== */
.obsce .obs-card {
  position: relative; background: linear-gradient(135deg, rgba(0,0,0,.04), rgba(0,0,0,0)), ${bone};
  color: #211d16; padding: 40px 22px 22px; transform: rotate(var(--rot));
  box-shadow: 0 14px 30px rgba(0,0,0,.6), 0 2px 4px rgba(0,0,0,.4);
  cursor: pointer; transition: transform .35s cubic-bezier(.34,1.3,.5,1), box-shadow .35s, filter .4s;
  border-radius: 1px; text-align: left; width: 100%; border: none; font: inherit;
}
.obsce .obs-card::after {
  content: ""; position: absolute; inset: 0; pointer-events: none; border-radius: 1px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='p'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.04 0.08' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23p)' opacity='0.5'/%3E%3C/svg%3E");
  opacity: .16; mix-blend-mode: multiply;
}
.obsce .obs-card:hover:not(.obs-locked) {
  transform: rotate(0deg) translateY(-6px) scale(1.025);
  box-shadow: 0 24px 50px rgba(0,0,0,.7), 0 0 0 1px ${amber}40; filter: brightness(1.04);
}
.obsce .obs-card.obs-locked { cursor: default; }
.obsce .obs-pin {
  width: 22px; height: 22px; border-radius: 50%;
  background: radial-gradient(circle at 35% 30%, #f0a868, ${amber} 45%, #6e3211 100%);
  box-shadow: 0 3px 5px rgba(0,0,0,.6), inset 0 1px 1px rgba(255,255,255,.4);
  position: absolute; top: -10px; left: 50%; z-index: 25;
  opacity: 0; transform: translateX(-50%) translateY(-8px) scale(.5);
  transition: opacity .35s, transform .4s cubic-bezier(.3,1.6,.5,1);
}
.obsce .obs-pin::after {
  content: ""; position: absolute; top: 18px; left: 50%; width: 2px; height: 7px;
  background: #2a2620; transform: translateX(-50%); border-radius: 0 0 2px 2px;
}
.obsce .obs-pin.obs-ox { background: radial-gradient(circle at 35% 30%, #b94a4a, ${oxblood} 45%, #3a0c0c 100%); }
.obsce .obs-card.obs-pinned .obs-pin { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
.obsce .obs-card.obs-pinned {
  transform: rotate(calc(var(--rot) * .4)) translateY(-4px);
  box-shadow: 0 22px 48px rgba(0,0,0,.75), 0 0 0 2px ${amber}, 0 0 26px ${amber}59; filter: saturate(1.05);
}
.obsce .obs-card-no {
  position: absolute; top: 14px; right: 14px; font-size: .7rem; letter-spacing: .15em;
  color: ${oxblood}; opacity: .7; transform: rotate(6deg); border: 1px solid ${oxblood}66; padding: 1px 5px;
}
.obsce .obs-pinned-tag {
  position: absolute; bottom: -12px; left: 50%; transform: translate(-50%,8px);
  font-size: .62rem; letter-spacing: .2em; text-transform: uppercase; background: ${amber}; color: #1a0f06;
  padding: 3px 9px; opacity: 0; transition: opacity .3s, transform .3s; white-space: nowrap; font-weight: 700;
  box-shadow: 0 4px 8px rgba(0,0,0,.5);
}
.obsce .obs-card.obs-pinned .obs-pinned-tag { opacity: 1; transform: translate(-50%,0); }

/* ===== CLAIM NODE ===== */
.obsce .obs-claim-node .obs-node-pin {
  width: 22px; height: 22px; border-radius: 50%; position: absolute; top: -11px; left: 50%; transform: translateX(-50%);
  background: radial-gradient(circle at 35% 30%, #b94a4a, ${oxblood} 45%, #3a0c0c 100%);
  box-shadow: 0 3px 5px rgba(0,0,0,.6), inset 0 1px 1px rgba(255,255,255,.4); z-index: 25;
}

/* ===== CASE CLOSED STAMP ===== */
.obsce .obs-stamp {
  position: absolute; top: 50%; left: 50%; z-index: 75;
  transform: translate(-50%,-50%) scale(4) rotate(-18deg); opacity: 0; pointer-events: none;
  font-weight: 700; font-size: clamp(2rem,7vw,5rem); letter-spacing: .08em; color: ${oxblood};
  border: 8px double ${oxblood}; padding: 18px 44px; border-radius: 8px; text-transform: uppercase;
  text-shadow: 2px 2px 0 rgba(0,0,0,.25);
}
.obsce .obs-stamp.obs-slam { animation: obsSlam .55s cubic-bezier(.2,1.4,.4,1) forwards; }
@keyframes obsSlam {
  0% { opacity: 0; transform: translate(-50%,-50%) scale(4) rotate(-18deg); }
  60% { opacity: .95; }
  78% { transform: translate(-50%,-50%) scale(.92) rotate(-13deg); opacity: 1; }
  100% { transform: translate(-50%,-50%) scale(1) rotate(-13deg); opacity: 1; }
}
.obsce .obs-slam-flash { position: absolute; inset: 0; z-index: 70; pointer-events: none; background: rgba(0,0,0,.6); opacity: 0; }
.obsce .obs-slam-flash.obs-go { animation: obsFlashFade .55s ease forwards; }
@keyframes obsFlashFade { 0% { opacity: .55; } 100% { opacity: 0; } }

/* ===== VERDICT / REDACTION REVEAL ===== */
.obsce .obs-verdict { opacity: 0; pointer-events: none; transform: translateY(30px); transition: opacity 1.4s ease, transform 1.4s ease; }
.obsce .obs-verdict.obs-show { opacity: 1; pointer-events: auto; transform: none; }
.obsce .obs-redline { position: relative; }
.obsce .obs-redline .obs-bar {
  position: absolute; inset: -1px -4px; z-index: 3; background: linear-gradient(180deg,#1a1712,#0b0b0d);
  transform-origin: left center; transition: transform 1s cubic-bezier(.7,0,.2,1); box-shadow: 0 1px 3px rgba(0,0,0,.5);
}
.obsce .obs-redline .obs-bar.obs-ox { background: linear-gradient(180deg,${oxblood},#3a0c0c); }
.obsce .obs-verdict.obs-lifted .obs-redline .obs-bar { transform: scaleX(0); }
.obsce .obs-rest-stamp {
  position: absolute; top: 14px; right: 14px; transform: rotate(11deg) scale(.5); opacity: .82; z-index: 8;
  font-weight: 700; font-size: 1.5rem; letter-spacing: .08em; color: ${oxblood};
  border: 5px double ${oxblood}; padding: 8px 18px; border-radius: 6px; text-transform: uppercase;
}

/* ===== STAGED VERDICT SEALS (§8.1b) ===== */
.obsce .obs-seal {
  display: flex; align-items: center; gap: 14px; width: 100%; text-align: left;
  border: none; cursor: pointer; margin-top: 18px; padding: 14px 18px;
  background: linear-gradient(180deg,#1a1712,#0b0b0d); color: #cfc8b8;
  font-size: 11px; letter-spacing: .3em; text-transform: uppercase; font-weight: 700;
  box-shadow: 0 2px 6px rgba(0,0,0,.5);
  transition: transform .25s, box-shadow .25s, opacity .25s;
}
.obsce .obs-seal:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 8px 18px rgba(0,0,0,.55), 0 0 0 1px ${amber}40; }
.obsce .obs-seal:disabled { cursor: default; opacity: .38; }
.obsce .obs-seal .obs-seal-tag { color: ${bone}; background: ${oxblood}; padding: 3px 8px; white-space: nowrap; }
.obsce .obs-seal-open { animation: obsSealOpen .6s ease both; }
@keyframes obsSealOpen { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }

/* ===== REDUCED MOTION ===== */
@media (prefers-reduced-motion: reduce) {
  .obsce .obs-strings path { stroke-dashoffset: 0 !important; transition: none !important; }
  .obsce .obs-card, .obsce .obs-pin, .obsce .obs-pinned-tag, .obsce .obs-verdict, .obsce .obs-redline .obs-bar, .obsce .obs-seal { transition: none !important; }
  .obsce .obs-stamp.obs-slam { animation: none !important; opacity: 1; transform: translate(-50%,-50%) rotate(-13deg); }
  .obsce .obs-slam-flash.obs-go { animation: none !important; }
  .obsce .obs-seal-open { animation: none !important; }
}
`;
}

function useScopedStyles(amber: string, brass: string, oxblood: string, twine: string, bone: string): void {
  useEffect(() => {
    let tag = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
    if (!tag) {
      tag = document.createElement('style');
      tag.id = STYLE_ID;
      tag.dataset.refcount = '0';
      tag.textContent = scopedCss(amber, brass, oxblood, twine, bone);
      document.head.appendChild(tag);
    }
    const count = Number(tag.dataset.refcount || '0') + 1;
    tag.dataset.refcount = String(count);
    return () => {
      const el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
      if (!el) return;
      const next = Number(el.dataset.refcount || '1') - 1;
      if (next <= 0) el.remove();
      else el.dataset.refcount = String(next);
    };
    // Token values are stable per theme; intentionally run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export function ObsidianClaimEvidence({
  data,
  theme,
  onActivityResponse,
  onComplete,
  savedResponse,
}: {
  data: ClaimEvidenceSlideData;
  theme: Theme;
  onActivityResponse?: (response: ActivityResponsePayload) => void;
  onComplete?: () => void;
  savedResponse?: Record<string, unknown> | null;
}) {
  const reduceMotion = usePrefersReducedMotion();

  // A saved {selectedEvidence, reasoning} payload describes the submitted
  // end-state — restore the closed-case view from it (mirrors ClaimEvidenceStudent).
  const savedSelected = Array.isArray(savedResponse?.selectedEvidence)
    ? (savedResponse!.selectedEvidence as string[])
    : null;
  const restored = !!savedSelected;

  // Noir palette — mapped from theme tokens with mockup-faithful constants.
  const amber = theme.colors.primary; // #d4682a
  const brass = theme.colors.accent || theme.colors.primary; // #b8924a
  const bone = theme.colors.text; // bone text → printed-paper background
  const oxblood = '#7a1f1f';
  const twine = '#9e2b21';
  const ash = theme.colors.textMuted;
  const headingFont = theme.fonts.heading;
  const bodyFont = theme.fonts.body;
  const typeFont = theme.fonts.code;

  useScopedStyles(amber, brass, oxblood, twine, bone);

  // ── Audio (toolkit) ──────────────────────────────────────────────────────────
  const { enabled: audioOn, toggle: toggleAudio, play } = useThemeWorldAudio('obsidian');

  // ── State machine (mirrors ClaimEvidenceStudent exactly) ─────────────────────
  const [selected, setSelected] = useState<Set<string>>(() => new Set(savedSelected ?? []));
  const [reasoning, setReasoning] = useState(
    restored && typeof savedResponse?.reasoning === 'string' ? (savedResponse.reasoning as string) : '',
  );
  const [submitted, setSubmitted] = useState(restored);
  // Fire onComplete exactly once at the terminal point (Close the Case).
  // Pre-seed the guard when restoring a saved submission so it never re-fires.
  const completedRef = useRef(restored);

  // ── Staged verdict (§8.1b) ───────────────────────────────────────────────────
  // The seals lift in this order and accumulate. A restored submission jumps
  // straight to the fully-lifted verdict (no replaying the taps).
  const sealOrder: SealKey[] = data.counterClaim
    ? ['judged', 'evidence', 'counter', 'reflect']
    : ['judged', 'evidence', 'reflect'];
  const [liftedCount, setLiftedCount] = useState(() => (restored ? sealOrder.length : 0));

  // The reflection is a real captured commit — restore it alongside the picks.
  const savedReflection =
    restored && typeof savedResponse?.reflection === 'string' ? (savedResponse.reflection as string) : '';
  const [reflectionNote, setReflectionNote] = useState(savedReflection);
  const [reflectionCommitted, setReflectionCommitted] = useState(savedReflection.trim().length > 0);

  // Shuffle the pool once per mount so authored order (strong → partial →
  // distractor in AI builds) can't leak evidence strength by position.
  const [shuffledPool] = useState(() => {
    const arr = [...data.evidencePool];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  });

  // Sanitize authored rules at read time — NaN or inverted min/max must never
  // brick the slide (mirrors ClaimEvidenceStudent).
  const poolSize = data.evidencePool.length;
  const rawMin = Number(data.argumentRules.minEvidence);
  const rawMax = Number(data.argumentRules.maxEvidence);
  let minEvidence = Number.isFinite(rawMin) ? Math.max(1, Math.round(rawMin)) : 1;
  let maxEvidence = Number.isFinite(rawMax) ? Math.max(1, Math.round(rawMax)) : Math.max(1, poolSize);
  if (poolSize > 0) {
    minEvidence = Math.min(minEvidence, poolSize);
    maxEvidence = Math.min(maxEvidence, poolSize);
  }
  if (minEvidence > maxEvidence) [minEvidence, maxEvidence] = [maxEvidence, minEvidence];
  const { requireReasoning } = data.argumentRules;
  const count = selected.size;
  const ready =
    count >= minEvidence &&
    count <= maxEvidence &&
    (!requireReasoning || reasoning.trim().length > 0);

  const expertSet = new Set(data.expertModel.selectedEvidence);
  const overlapCount = [...selected].filter((id) => expertSet.has(id)).length;

  // ── String drawing (SVG paths card → claim node) ─────────────────────────────
  const boardRef = useRef<HTMLDivElement | null>(null);
  const claimNodeRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [strings, setStrings] = useState<Array<{ id: string; d: string; len: number }>>([]);
  const [drawnIds, setDrawnIds] = useState<Set<string>>(new Set());

  const centerOf = useCallback((el: HTMLElement, board: HTMLElement): { x: number; y: number } => {
    const br = board.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    return { x: r.left - br.left + r.width / 2, y: r.top - br.top + r.height / 2 };
  }, []);

  // Recompute every string geometry from the current pinned set.
  const recomputeStrings = useCallback(() => {
    const board = boardRef.current;
    const node = claimNodeRef.current;
    if (!board || !node) return;
    const b = centerOf(node, board);
    const next: Array<{ id: string; d: string; len: number }> = [];
    selected.forEach((id) => {
      const card = cardRefs.current.get(id);
      if (!card) return;
      const a = centerOf(card, board);
      const midX = (a.x + b.x) / 2;
      const midY = (a.y + b.y) / 2 + 22; // gentle sag
      const d = `M ${a.x} ${a.y} Q ${midX} ${midY} ${b.x} ${b.y}`;
      // chord length is a good-enough dash length for the unspooling effect
      const len = Math.hypot(b.x - a.x, b.y - a.y) + 24;
      next.push({ id, d, len });
    });
    setStrings(next);
  }, [selected, centerOf]);

  // Geometry depends on layout — measure after paint, and on resize.
  useLayoutEffect(() => {
    recomputeStrings();
  }, [recomputeStrings]);

  useEffect(() => {
    const onResize = () => recomputeStrings();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [recomputeStrings]);

  // Animate the unspool: paths mount at full dashoffset, then flip to drawn on
  // the next frame (or immediately under reduced motion).
  useEffect(() => {
    if (reduceMotion) {
      setDrawnIds(new Set(strings.map((s) => s.id)));
      return;
    }
    const raf = requestAnimationFrame(() => {
      setDrawnIds(new Set(strings.map((s) => s.id)));
    });
    return () => cancelAnimationFrame(raf);
  }, [strings, reduceMotion]);

  // ── Pin / unpin (mirrors ClaimEvidenceStudent.toggle + mockup limit shake) ───
  const [hint, setHint] = useState<string>('');
  const togglePin = useCallback(
    (id: string) => {
      if (submitted) return;
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else if (next.size < maxEvidence) {
          next.add(id);
          play('select'); // soft pin-push click
        } else {
          setHint(`That's the limit (${maxEvidence}). Unpin something before adding more.`);
          return prev;
        }
        return next;
      });
    },
    [submitted, maxEvidence, play],
  );

  // ── Submit (EXACT contract from ClaimEvidenceStudent) ────────────────────────
  const [slam, setSlam] = useState(false);
  const [stampVisible, setStampVisible] = useState(false);
  // When restoring a saved submission, jump straight to the unsealed verdict
  // (no slam/animation replay).
  const [verdictShow, setVerdictShow] = useState(restored);
  const [verdictLifted, setVerdictLifted] = useState(restored);
  const [restStamp, setRestStamp] = useState(restored);
  const submitTimers = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  useEffect(() => {
    const timers = submitTimers.current;
    return () => timers.forEach((t) => clearTimeout(t));
  }, []);

  const submit = useCallback(() => {
    if (!ready || submitted) return;
    setSubmitted(true);
    // Local grading — set-equality vs the expert picks (mirrors ClaimEvidenceStudent).
    const expert = new Set(data.expertModel.selectedEvidence);
    const intersection = [...selected].filter((id) => expert.has(id)).length;
    const isCorrect = selected.size === expert.size && intersection === expert.size;
    const score = expert.size > 0 ? Math.round((intersection / expert.size) * 100) : 0;
    onActivityResponse?.({
      activityType: 'claim-evidence',
      mode: 'quiz',
      responseData: { selectedEvidence: Array.from(selected), reasoning },
      isCorrect,
      score,
    });
    // Terminal completion point: closing the case is the single submit.
    if (!completedRef.current) {
      completedRef.current = true;
      onComplete?.();
    }

    // CASE CLOSED stamp slams + heavy thud
    play('commit');
    if (reduceMotion) {
      setStampVisible(true);
      setRestStamp(true);
      setVerdictShow(true);
      setVerdictLifted(true);
      play('reveal');
      return;
    }

    setSlam(true);
    setStampVisible(true);
    const t1 = setTimeout(() => {
      setSlam(false);
      setStampVisible(false);
      setRestStamp(true);
      setVerdictShow(true);
      play('reveal'); // single one-shot cello swell on the unsealing
      const t2 = setTimeout(() => {
        setVerdictLifted(true); // redaction bars lift
        play('advance'); // paper rustle
      }, 700);
      submitTimers.current.push(t2);
    }, 620);
    submitTimers.current.push(t1);
  }, [ready, submitted, onActivityResponse, onComplete, selected, reasoning, play, reduceMotion, data.expertModel.selectedEvidence]);

  // ── Lift a seal (§8.1b staged verdict) ───────────────────────────────────────
  // Seals lift strictly in order; lifted sections accumulate. Each lift plays
  // the paper-rustle cue ('advance' — the same rustle the score reveal uses).
  const liftSeal = useCallback(
    (index: number) => {
      if (index !== liftedCount) return;
      setLiftedCount(index + 1);
      play('advance');
    },
    [liftedCount, play],
  );

  // ── Commit the reflection (real capture, not display-only) ───────────────────
  // Activity records are keyed per slide, so this re-emits the FULL submit
  // payload with the reflection appended — the overwrite is a strict superset
  // of the Close-the-Case record (selectedEvidence + reasoning preserved, same
  // grading), so rehydration and reporting keep working unchanged.
  const commitReflection = useCallback(() => {
    const note = reflectionNote.trim();
    if (!note || !submitted || reflectionCommitted) return;
    const expert = new Set(data.expertModel.selectedEvidence);
    const intersection = [...selected].filter((id) => expert.has(id)).length;
    const isCorrect = selected.size === expert.size && intersection === expert.size;
    const score = expert.size > 0 ? Math.round((intersection / expert.size) * 100) : 0;
    onActivityResponse?.({
      activityType: 'claim-evidence',
      mode: 'quiz',
      responseData: { selectedEvidence: Array.from(selected), reasoning, reflection: note },
      isCorrect,
      score,
    });
    setReflectionCommitted(true);
    play('commit');
  }, [reflectionNote, submitted, reflectionCommitted, data.expertModel.selectedEvidence, selected, reasoning, onActivityResponse, play]);

  // ── Reopen (mirrors mockup reopen / resets state machine) ────────────────────
  const reopen = useCallback(() => {
    play('reset');
    setSubmitted(false);
    setSelected(new Set());
    setReasoning('');
    setStrings([]);
    setDrawnIds(new Set());
    setVerdictShow(false);
    setVerdictLifted(false);
    setRestStamp(false);
    setStampVisible(false);
    setHint('');
    setLiftedCount(0);
    setReflectionNote('');
    setReflectionCommitted(false);
  }, [play]);

  const missedStrong = data.evidencePool.filter((e) => e.strength === 'strong' && !selected.has(e.id));

  return (
    <div
      className="obsce h-full w-full overflow-y-auto"
      style={{
        background: `radial-gradient(circle at 50% 38%, ${amber}1f 0%, ${theme.colors.background} 60%), ${theme.colors.background}`,
        color: bone,
        fontFamily: bodyFont,
      }}
    >
      {/* ===== ATMOSPHERE (toolkit) ===== */}
      <ThemeWorldAtmosphere
        worldId="obsidian"
        primary={amber}
        accent={brass}
        background={theme.colors.background}
        style={{ position: 'fixed', zIndex: 0 }}
      />

      {/* ===== SOUND TOGGLE (toolkit) ===== */}
      <SoundToggle enabled={audioOn} onToggle={toggleAudio} theme={theme} />

      {/* slam flash + stamp (fixed within slide) */}
      <div className={`obs-slam-flash${slam ? ' obs-go' : ''}`} aria-hidden />
      {stampVisible && (
        <div className={`obs-stamp${slam ? ' obs-slam' : ''}`} style={{ fontFamily: typeFont }} aria-hidden>
          Case Closed
        </div>
      )}

      <div className="relative" style={{ zIndex: 10 }}>
        <div className="mx-auto" style={{ maxWidth: 1180, padding: '64px 24px 120px' }}>
          {/* ===== CASE HEAD ===== */}
          <header style={{ textAlign: 'center', marginBottom: 26 }}>
            <span
              style={{
                fontFamily: typeFont,
                fontSize: 11,
                letterSpacing: '0.42em',
                color: brass,
                textTransform: 'uppercase',
                display: 'inline-block',
                padding: '5px 16px',
                border: `1px solid ${brass}66`,
                marginBottom: 22,
              }}
            >
              ● Case File — The Claim
            </span>
            <div style={{ fontFamily: typeFont, fontSize: 12, letterSpacing: '0.3em', color: ash, textTransform: 'uppercase', marginBottom: 10 }}>
              The Claim
            </div>
            <h1 style={{ margin: 0 }}>
              <Typewriter
                text={data.claim.statement}
                speed={32}
                caretColor={amber}
                style={{
                  fontFamily: headingFont,
                  fontWeight: 600,
                  fontSize: 'clamp(2rem, 5.4vw, 4.1rem)',
                  lineHeight: 1.05,
                  color: amber,
                  letterSpacing: '0.01em',
                  textShadow: `0 2px 28px ${amber}47`,
                  display: 'inline-block',
                }}
              />
            </h1>
            {data.claim.context && (
              <p
                style={{
                  fontFamily: bodyFont,
                  fontStyle: 'italic',
                  fontSize: 'clamp(1rem, 1.8vw, 1.22rem)',
                  lineHeight: 1.55,
                  color: theme.colors.text,
                  opacity: 0.92,
                  maxWidth: '60ch',
                  margin: '24px auto 0',
                }}
              >
                {data.claim.context}
              </p>
            )}
          </header>

          {/* ===== BOARD ===== */}
          <section ref={boardRef} style={{ position: 'relative', maxWidth: 1180, margin: '38px auto 0' }}>
            <svg className="obs-strings" preserveAspectRatio="none" aria-hidden>
              {strings.map((s) => (
                <path
                  key={s.id}
                  className={drawnIds.has(s.id) ? 'obs-drawn' : undefined}
                  d={s.d}
                  style={{ ['--len' as string]: String(s.len) }}
                />
              ))}
            </svg>

            {/* central claim node */}
            <div
              ref={claimNodeRef}
              className="obs-claim-node"
              style={{
                position: 'relative',
                zIndex: 20,
                width: 'min(360px, 80vw)',
                margin: '0 auto 46px',
                background: 'linear-gradient(160deg,#1c1c22,#101014)',
                border: `1px solid ${amber}73`,
                boxShadow: `0 0 0 1px rgba(0,0,0,.5), 0 18px 46px rgba(0,0,0,.7), inset 0 0 40px ${amber}0f`,
                padding: '22px 26px',
                textAlign: 'center',
              }}
            >
              <span className="obs-node-pin" aria-hidden />
              <h2 style={{ fontFamily: typeFont, fontSize: 11, letterSpacing: '0.34em', color: amber, textTransform: 'uppercase', margin: '6px 0 8px' }}>
                The Verdict at Stake
              </h2>
              <p style={{ fontFamily: headingFont, fontSize: '1.35rem', lineHeight: 1.2, color: bone, margin: 0, fontWeight: 500 }}>
                {data.claim.statement}
              </p>
            </div>

            <div style={{ textAlign: 'center', fontFamily: typeFont, fontSize: 12, letterSpacing: '0.34em', color: ash, textTransform: 'uppercase', margin: '14px 0 28px' }}>
              Evidence on the board — <span style={{ color: amber }}>{count}</span> pinned ({minEvidence}–{maxEvidence})
            </div>

            {/* evidence grid */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(248px, 1fr))',
                gap: '34px 30px',
                position: 'relative',
                zIndex: 10,
              }}
            >
              {shuffledPool.map((ev, idx) => {
                const isPinned = selected.has(ev.id);
                const rot = ((idx % 2 === 0 ? 1 : -1) * (0.8 + ((idx * 37) % 22) / 10)).toFixed(2);
                // Pin appearance must NOT vary by strength before submit — that
                // would leak the answer. All board pins use the neutral amber.
                const pinClass = 'obs-pin';
                return (
                  <button
                    key={ev.id}
                    ref={(el) => {
                      if (el) cardRefs.current.set(ev.id, el);
                      else cardRefs.current.delete(ev.id);
                    }}
                    type="button"
                    onClick={() => togglePin(ev.id)}
                    onMouseEnter={() => play('hover')}
                    disabled={submitted}
                    aria-pressed={isPinned}
                    className={`obs-card${isPinned ? ' obs-pinned' : ''}${submitted ? ' obs-locked' : ''}`}
                    style={{ ['--rot' as string]: `${rot}deg`, fontFamily: bodyFont }}
                  >
                    <span className={pinClass} aria-hidden />
                    <span className="obs-card-no" style={{ fontFamily: typeFont }}>
                      EX-{String(idx + 1).padStart(2, '0')}
                    </span>
                    <h3 style={{ fontFamily: headingFont, fontWeight: 700, fontSize: '1.7rem', lineHeight: 1.1, color: '#1a1712', margin: '0 0 11px' }}>
                      {ev.label}
                    </h3>
                    <p style={{ fontFamily: typeFont, fontSize: '.98rem', lineHeight: 1.5, color: '#37322a', margin: 0 }}>
                      {ev.detail}
                    </p>
                    <span className="obs-pinned-tag" style={{ fontFamily: typeFont }}>
                      Pinned to claim
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          {/* ===== DESK : REASONING + CLOSE ===== */}
          {!submitted && (
            <section style={{ maxWidth: 760, margin: '64px auto 0', textAlign: 'center' }}>
              {requireReasoning && (
                <>
                  <label
                    htmlFor="obs-reason"
                    style={{ display: 'block', fontFamily: typeFont, fontSize: 11, letterSpacing: '0.3em', textTransform: 'uppercase', color: ash, marginBottom: 14 }}
                  >
                    Your reasoning — one line, for the record
                  </label>
                  <textarea
                    id="obs-reason"
                    value={reasoning}
                    onChange={(e) => setReasoning(e.target.value)}
                    rows={2}
                    placeholder="The evidence I pinned holds because…"
                    style={{
                      width: '100%',
                      background: `${bone}0f`,
                      border: 'none',
                      borderBottom: `2px solid ${brass}80`,
                      color: bone,
                      fontFamily: typeFont,
                      fontSize: '1rem',
                      padding: '14px 16px',
                      resize: 'none',
                      lineHeight: 1.6,
                      outline: 'none',
                    }}
                  />
                </>
              )}
              <button
                type="button"
                onClick={submit}
                disabled={!ready}
                style={{
                  marginTop: 34,
                  fontFamily: typeFont,
                  fontSize: 14,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  color: bone,
                  background: oxblood,
                  border: `1px solid ${amber}66`,
                  padding: '18px 42px',
                  cursor: ready ? 'pointer' : 'not-allowed',
                  opacity: ready ? 1 : 0.32,
                  boxShadow: `0 10px 30px ${oxblood}73, inset 0 1px 0 rgba(255,255,255,.12)`,
                }}
              >
                Close the Case
              </button>
              <div style={{ marginTop: 16, fontFamily: bodyFont, fontStyle: 'italic', fontSize: '.92rem', color: hint ? amber : ash, minHeight: '1.2em' }}>
                {hint ||
                  (count < minEvidence
                    ? `Pin between ${minEvidence} and ${maxEvidence} pieces of evidence to proceed.`
                    : 'Write your reasoning, then close the case.')}
              </div>
            </section>
          )}

          {/* ===== VERDICT / EXPERT MODEL REVEAL ===== */}
          {submitted && (
            <section className={`obs-verdict${verdictShow ? ' obs-show' : ''}${verdictLifted ? ' obs-lifted' : ''}`} style={{ maxWidth: 900, margin: '70px auto 0' }}>
              <div
                style={{
                  position: 'relative',
                  background: bone,
                  color: '#211d16',
                  padding: '48px clamp(24px, 5vw, 64px) 54px',
                  boxShadow: '0 30px 70px rgba(0,0,0,.8)',
                  transform: 'rotate(-.6deg)',
                }}
              >
                {restStamp && (
                  <span className="obs-rest-stamp" style={{ fontFamily: typeFont }} aria-hidden>
                    Case Closed
                  </span>
                )}
                <h3 style={{ fontFamily: typeFont, fontSize: 12, letterSpacing: '0.4em', color: oxblood, textTransform: 'uppercase', textAlign: 'center', margin: '0 0 6px' }}>
                  The Expert Model — Unsealed
                </h3>
                <div style={{ fontFamily: headingFont, fontWeight: 700, fontSize: '2.4rem', textAlign: 'center', color: '#1a1712', margin: '0 0 26px', lineHeight: 1 }}>
                  The Verdict
                </div>

                {/* beat 1 — the score line ALONE; redaction bar lifts on reveal */}
                <p className="obs-redline" style={{ fontFamily: bodyFont, fontSize: '1.12rem', lineHeight: 1.85, color: '#2a251c', margin: '0 0 12px' }}>
                  <span className="obs-bar" aria-hidden />
                  You matched <strong style={{ color: oxblood }}>{overlapCount}</strong> of{' '}
                  <strong style={{ color: oxblood }}>{data.expertModel.selectedEvidence.length}</strong> pieces the expert pinned.
                </p>

                {/* beats 2+ — redaction seals, tapped open in order; lifted sections accumulate (§8.1b) */}
                {sealOrder.map((key, i) => {
                  if (i >= liftedCount) {
                    return (
                      <button
                        key={key}
                        type="button"
                        className="obs-seal"
                        style={{ fontFamily: typeFont }}
                        disabled={i !== liftedCount}
                        onClick={() => liftSeal(i)}
                      >
                        <span className="obs-seal-tag">Sealed</span>
                        <span style={{ flex: 1 }}>{SEAL_LABEL[key]}</span>
                        <span style={{ opacity: 0.6 }} aria-hidden>
                          {i === liftedCount ? 'Tap to lift' : '▮▮▮▮'}
                        </span>
                      </button>
                    );
                  }

                  return (
                    <div key={key} className="obs-seal-open">
                      {/* user's picks, judged by real strength — personal first */}
                      {key === 'judged' && (
                        <div style={{ marginTop: 32, paddingTop: 24, borderTop: '1px dashed rgba(60,50,30,.4)' }}>
                          <h4 style={{ fontFamily: typeFont, fontSize: 11, letterSpacing: '0.3em', textTransform: 'uppercase', color: '#5a5343', margin: '0 0 16px' }}>
                            Your Picks, Judged
                          </h4>
                          {data.evidencePool
                            .filter((ev) => selected.has(ev.id))
                            .map((ev) => {
                              const fb = feedbackStyle(
                                theme,
                                ev.strength === 'strong' ? 'correct' : ev.strength === 'partial' ? 'warning' : 'incorrect',
                              );
                              const tagBg = ev.strength === 'strong' ? amber : ev.strength === 'partial' ? brass : oxblood;
                              return (
                                <div
                                  key={ev.id}
                                  style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 0', borderBottom: '1px solid rgba(60,50,30,.18)', fontFamily: bodyFont, fontSize: '1.02rem', color: '#2a251c' }}
                                >
                                  <span
                                    style={{
                                      fontFamily: typeFont,
                                      fontSize: '.66rem',
                                      letterSpacing: '0.16em',
                                      textTransform: 'uppercase',
                                      padding: '4px 9px',
                                      fontWeight: 700,
                                      whiteSpace: 'nowrap',
                                      minWidth: 78,
                                      textAlign: 'center',
                                      background: tagBg,
                                      color: ev.strength === 'distractor' ? '#f0e6d6' : '#1a0f06',
                                      // borrow the themed feedback border for a subtle ring
                                      boxShadow: `inset 0 0 0 1px ${fb.border}`,
                                    }}
                                  >
                                    {STRENGTH_LABEL[ev.strength]}
                                  </span>
                                  <span style={{ flex: 1 }}>{ev.label}</span>
                                </div>
                              );
                            })}
                          {missedStrong.length > 0 && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '10px 0', opacity: 0.7, fontFamily: bodyFont, fontSize: '1.02rem', color: '#2a251c' }}>
                              <span style={{ fontFamily: typeFont, fontSize: '.66rem', letterSpacing: '0.16em', textTransform: 'uppercase', padding: '4px 9px', fontWeight: 700, whiteSpace: 'nowrap', minWidth: 78, textAlign: 'center', background: '#3a352b', color: '#cfc8b8' }}>
                                Missed
                              </span>
                              <span style={{ flex: 1, fontStyle: 'italic' }}>
                                {missedStrong.map((e) => e.label).join(', ')} — strong evidence you left on the board.
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* the evidence that holds — expert reasoning + per-evidence justification */}
                      {key === 'evidence' && (
                        <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px dashed rgba(60,50,30,.4)' }}>
                          <div style={{ fontFamily: typeFont, fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase', color: '#5a5343', margin: '0 0 10px' }}>
                            The evidence that holds
                          </div>
                          {data.expertModel.reasoning && (
                            <p style={{ fontFamily: bodyFont, fontSize: '1.12rem', lineHeight: 1.85, color: '#2a251c', margin: '0 0 12px' }}>
                              {data.expertModel.reasoning}
                            </p>
                          )}
                          <ul style={{ margin: '0 0 4px', paddingLeft: 20 }}>
                            {data.expertModel.selectedEvidence.map((id) => {
                              const ev = data.evidencePool.find((e) => e.id === id);
                              if (!ev) return null;
                              return (
                                <li key={id} style={{ fontFamily: bodyFont, fontSize: '1rem', lineHeight: 1.6, color: '#2a251c', marginBottom: 4 }}>
                                  <strong>{ev.label}</strong>
                                  {ev.expertReasoning ? ` — ${ev.expertReasoning}` : ''}
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}

                      {/* counter-claim */}
                      {key === 'counter' && data.counterClaim && (
                        <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px dashed rgba(60,50,30,.4)' }}>
                          <div style={{ fontFamily: typeFont, fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase', color: '#5a5343', marginBottom: 8 }}>
                            Counter-Claim
                          </div>
                          <p style={{ fontFamily: bodyFont, fontWeight: 600, fontSize: '1.02rem', color: '#1a1712', margin: '0 0 6px' }}>
                            {data.counterClaim.statement}
                          </p>
                          <p style={{ fontFamily: typeFont, fontSize: '.85rem', color: '#5a5343', margin: 0 }}>
                            Supported by:{' '}
                            {data.counterClaim.strongEvidence
                              .map((id) => data.evidencePool.find((e) => e.id === id)?.label)
                              .filter(Boolean)
                              .join(', ')}
                          </p>
                        </div>
                      )}

                      {/* reflect (real captured commit) + reopen */}
                      {key === 'reflect' && (
                        <div style={{ marginTop: 28, paddingTop: 20, borderTop: '1px dashed rgba(60,50,30,.4)', textAlign: 'center' }}>
                          {data.reflection?.prompt && (
                            <>
                              <div style={{ fontFamily: typeFont, fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase', color: oxblood, marginBottom: 8 }}>
                                Reflect
                              </div>
                              <p style={{ fontFamily: bodyFont, fontStyle: 'italic', fontSize: '1.02rem', color: '#2a251c', margin: '0 0 14px' }}>
                                {data.reflection.prompt}
                              </p>
                              {reflectionCommitted ? (
                                <p style={{ fontFamily: bodyFont, fontSize: '1.02rem', color: '#2a251c', margin: 0 }}>
                                  <span style={{ fontFamily: typeFont, fontSize: '.66rem', letterSpacing: '0.16em', textTransform: 'uppercase', padding: '4px 9px', fontWeight: 700, background: oxblood, color: '#f0e6d6', marginRight: 10, whiteSpace: 'nowrap' }}>
                                    On the record
                                  </span>
                                  {reflectionNote}
                                </p>
                              ) : (
                                <>
                                  <textarea
                                    value={reflectionNote}
                                    onChange={(e) => setReflectionNote(e.target.value)}
                                    rows={2}
                                    placeholder="Your answer, for the record"
                                    aria-label="Your reflection, for the record"
                                    style={{
                                      width: '100%',
                                      background: 'rgba(0,0,0,.05)',
                                      border: 'none',
                                      borderBottom: `2px solid ${oxblood}80`,
                                      color: '#211d16',
                                      fontFamily: typeFont,
                                      fontSize: '1rem',
                                      padding: '12px 14px',
                                      resize: 'none',
                                      lineHeight: 1.6,
                                      outline: 'none',
                                    }}
                                  />
                                  <button
                                    type="button"
                                    onClick={commitReflection}
                                    disabled={!reflectionNote.trim()}
                                    style={{
                                      display: 'block',
                                      margin: '14px auto 0',
                                      fontFamily: typeFont,
                                      fontSize: 12,
                                      letterSpacing: '0.24em',
                                      textTransform: 'uppercase',
                                      fontWeight: 700,
                                      color: '#f0e6d6',
                                      background: oxblood,
                                      border: 'none',
                                      padding: '12px 26px',
                                      cursor: reflectionNote.trim() ? 'pointer' : 'not-allowed',
                                      opacity: reflectionNote.trim() ? 1 : 0.4,
                                    }}
                                  >
                                    Add to the Record
                                  </button>
                                </>
                              )}
                            </>
                          )}
                          <button
                            type="button"
                            onClick={reopen}
                            style={{
                              display: 'block',
                              margin: '40px auto 0',
                              fontFamily: typeFont,
                              fontSize: 13,
                              letterSpacing: '0.28em',
                              textTransform: 'uppercase',
                              color: '#3a352b',
                              background: 'transparent',
                              border: '1px solid rgba(60,50,30,.4)',
                              padding: '14px 34px',
                              cursor: 'pointer',
                            }}
                          >
                            ↺ Reopen the Case
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
